/**
 * Meeting Post Edit Page
 * /meetings/:id/posts/:postId/edit - 모임 게시글 수정 페이지
 */

import { MarketImageUploader } from "@/components/MarketImageUploader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { buildLoginPath } from "@/lib/auth-navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useRoute } from "wouter";
import { z } from "zod";

const formSchema = z.object({
  title: z
    .string()
    .max(200, "제목은 200자 이하로 입력해주세요")
    .optional()
    .or(z.literal("")),
  content: z.string().min(10, "내용은 최소 10자 이상이어야 합니다").max(5000),
  category: z
    .enum(["free", "notice", "review", "schedule_review", "join_greeting"])
    .default("free"),
  images: z.array(z.string()).optional().default([]),
});

type FormData = z.infer<typeof formSchema>;

interface Meeting {
  id: string;
  title: string;
  hostId?: string | null;
  hostEmail?: string | null;
}

export default function MeetingPostEditPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ id: string; postId: string }>("/meetings/:id/posts/:postId/edit");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [images, setImages] = useState<string[]>([]);

  const meetingId = params?.id;
  const postId = params?.postId;

  // 모임 상세 조회
  const { data: meetingData, isLoading: meetingLoading } = useQuery<{
    ok: boolean;
    data: Meeting;
  }>({
    queryKey: ["/api/meetings", meetingId],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const response = await fetch(`/api/meetings/${meetingId}`);
      if (!response.ok) throw new Error("Failed to fetch meeting");
      return response.json();
    },
    enabled: !!meetingId,
  });

  // 게시글 조회
  const { data: postData, isLoading: postLoading } = useQuery<{
    ok: boolean;
    data: {
      meetingId: string;
      meetingTitle: string;
      post: {
        id: string;
        title: string | null;
        content: string;
        images: string[];
        category: string;
        authorId: string | null;
      };
    };
  }>({
    queryKey: ["/api/meetings", meetingId, "posts", postId],
    queryFn: async () => {
      if (!meetingId || !postId) throw new Error("Invalid request");
      const res = await fetch(`/api/meetings/${meetingId}/posts/${postId}`);
      if (!res.ok) throw new Error("Failed to load post");
      return res.json();
    },
    enabled: !!meetingId && !!postId,
  });

  const meeting = meetingData?.ok ? meetingData.data : null;
  const post = postData?.ok ? postData.data?.post : null;

  // 작성자 확인
  const isAuthor = user && post && post.authorId && user.uid === post.authorId;

  // 작성자가 아니면 접근 불가
  useEffect(() => {
    if (post && !isAuthor) {
      toast({
        title: "권한 없음",
        description: "본인이 작성한 게시글만 수정할 수 있습니다.",
        variant: "destructive",
      });
      navigate(`/meetings/${meetingId}/posts/${postId}`);
    }
  }, [post, isAuthor, meetingId, postId, navigate, toast]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: "free",
      images: [],
    },
  });

  // 게시글 데이터를 폼에 로드
  useEffect(() => {
    if (post) {
      setValue("title", post.title || "");
      setValue("content", post.content);
      setValue("category", post.category as any);
      setValue("images", post.images || []);
      setImages(post.images || []);
    }
  }, [post, setValue]);

  const postCategory = watch("category");
  const isHost =
    !!user &&
    !!meeting &&
    ((meeting.hostId && meeting.hostId === user.uid) ||
      (meeting.hostEmail &&
        user.email &&
        meeting.hostEmail.toLowerCase() === user.email.toLowerCase()));

  // 이미지 변경 핸들러
  const handleImagesChange = (newImages: string[]) => {
    setImages(newImages);
    setValue("images", newImages);
  };

  // 게시글 수정
  const updatePostMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!user) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();

      const response = await fetch(
        `/api/meetings/${meetingId}/posts/${postId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: data.title || null,
            content: data.content,
            category: data.category,
            images: data.images || images || [],
            meetingId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "게시글 수정에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "게시글 수정 완료",
        description: "게시글이 수정되었습니다.",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/meetings", meetingId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/meetings", meetingId, "posts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/meetings", meetingId, "posts", postId],
      });
      navigate(`/meetings/${meetingId}/posts/${postId}`);
    },
    onError: (error: Error) => {
      toast({
        title: "게시글 수정 실패",
        description: error.message || "게시글 수정 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    if (!user) {
      toast({
        title: "로그인 필요",
        description: "게시글을 수정하려면 로그인이 필요합니다.",
        variant: "destructive",
      });
      navigate(buildLoginPath());
      return;
    }

    // 이미지가 업로드 중인지 확인 (blob URL이 있으면 업로드 중)
    const hasUploadingImages = images.some((url) => url && url.startsWith("blob:"));
    if (hasUploadingImages) {
      toast({
        title: "이미지 업로드 대기",
        description: "이미지 업로드가 완료될 때까지 기다려주세요.",
        variant: "destructive",
      });
      return;
    }

    // data.images 또는 images state 중 실제 업로드된 이미지만 사용 (blob URL 제외)
    const finalImages = (data.images || images || []).filter(
      (url: string) => url && !url.startsWith("blob:")
    );

    updatePostMutation.mutate({
      ...data,
      images: finalImages,
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-bold mb-2">로그인이 필요합니다</h2>
          <p className="text-muted-foreground mb-4">
            게시글을 수정하려면 먼저 로그인해주세요.
          </p>
          <Button onClick={() => navigate(buildLoginPath())}>로그인하기</Button>
        </Card>
      </div>
    );
  }

  if (meetingLoading || postLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">로딩 중...</div>
      </div>
    );
  }

  if (!meeting || !post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-bold mb-2">게시글을 찾을 수 없습니다</h2>
          <Button onClick={() => navigate(`/meetings/${meetingId}`)}>
            목록으로
          </Button>
        </Card>
      </div>
    );
  }

  if (!isAuthor) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-bold mb-2">권한 없음</h2>
          <p className="text-muted-foreground mb-4">
            본인이 작성한 게시글만 수정할 수 있습니다.
          </p>
          <Button onClick={() => navigate(`/meetings/${meetingId}/posts/${postId}`)}>
            게시글 보기
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 헤더 */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/meetings/${meetingId}/posts/${postId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold">게시글 수정</h1>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle>{meeting.title} - 게시글 수정</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* 카테고리 선택 */}
              <div className="space-y-2">
                <Label>카테고리</Label>
                <Select
                  value={postCategory}
                  onValueChange={(value) => setValue("category", value as any)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">자유</SelectItem>
                    {isHost && (
                      <SelectItem value="notice">공지사항</SelectItem>
                    )}
                    <SelectItem value="review">모임 후기</SelectItem>
                    <SelectItem value="schedule_review">일정 후기</SelectItem>
                    <SelectItem value="join_greeting">가입인사</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="text-sm text-destructive">
                    {errors.category.message}
                  </p>
                )}
              </div>

              {/* 제목 (선택사항) */}
              <div className="space-y-2">
                <Label>제목 (선택사항)</Label>
                <Input
                  {...register("title")}
                  placeholder="제목을 입력하세요 (선택사항)"
                />
                {errors.title && (
                  <p className="text-sm text-destructive">
                    {errors.title.message}
                  </p>
                )}
              </div>

              {/* 내용 */}
              <div className="space-y-2">
                <Label>내용 *</Label>
                <Textarea
                  {...register("content")}
                  placeholder="게시글 내용을 입력하세요 (최소 10자)"
                  rows={10}
                  className="resize-none"
                />
                {errors.content && (
                  <p className="text-sm text-destructive">
                    {errors.content.message}
                  </p>
                )}
              </div>

              {/* 사진 업로드 */}
              <div className="space-y-2">
                <Label>사진 (선택사항)</Label>
                <MarketImageUploader
                  maxImages={10}
                  onImagesChange={handleImagesChange}
                  userId={user?.uid}
                  initialImages={post?.images || []}
                />
              </div>

              {/* 버튼 */}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/meetings/${meetingId}/posts/${postId}`)}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={updatePostMutation.isPending}
                >
                  {updatePostMutation.isPending ? "수정 중..." : "수정하기"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
